import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { readGuest, writeGuest } from "@/lib/guest";
import api, { API } from "@/lib/api";
import AmbientMixer from "@/lib/ambientMixer";
import { setSessionMetadata, clearSessionMetadata, bindMediaSessionControls, attachAmbientStreamToAudio } from "@/lib/mediaSession";
import { startKeepAlive, stopKeepAlive } from "@/lib/silentKeepAlive";
import { retryOnNextGesture } from "@/lib/audioUnlock";
import { Moon, Sparkles, BookmarkPlus, ArrowLeft, Pause, Play } from "lucide-react";

const MODE_LABEL = { sleep: "Sleep", quiet_presence: "Sleep", roleplay: "Sleep" };

// Elapsed-driven stage copy for the first-meeting generation wait. The trial
// generation is one long synchronous call (~25-95s) — without visible motion
// the screen reads as frozen (App Store review stall). Stages + progress keep
// it alive and honest.
const GEN_STAGES = [
  [0, "She's settling in"],
  [12, "She's thinking about what to say"],
  [30, "Warming her voice"],
  [55, "Almost there — a long thought tonight"],
  [90, "Still here — the first night takes the longest"],
];
const stageFor = (s) => {
  let label = GEN_STAGES[0][1];
  for (const [t, l] of GEN_STAGES) if (s >= t) label = l;
  return label;
};

/**
 * Guest first-session experience — instant, cinematic entry.
 *
 * The user lands here the moment they tap "Begin". Ambient audio starts
 * immediately (they hear the world before a word is spoken); the dialogue is
 * generated in the background and its voice fades in when ready. No "loading"
 * button ever blocks the doorway — we simply light the fire and let them in.
 */
export default function MeetSession() {
  const nav = useNavigate();
  const guest = readGuest();
  const atmosphere = guest.atmosphere;
  const characterId = guest.character_id;

  const mixerRef = useRef(null);
  const audioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const fadeRef = useRef(null);
  const startedRef = useRef(false);

  const [dialogue, setDialogue] = useState(guest.narration || null);
  const [generating, setGenerating] = useState(!guest.narration);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(true);
  // Stabilization pass — the Save card must never be the FIRST thing a new
  // user meaningfully sees: it appears once the companion's voice has
  // actually begun (with a grace fallback so a failed TTS never dead-ends).
  const [voiceStarted, setVoiceStarted] = useState(false);
  const [savePromptDue, setSavePromptDue] = useState(false);
  // App Review resilience — generation failure surfaces an in-place retry
  // instead of silently bouncing back to /meet; elapsed drives stage copy.
  const [genFailed, setGenFailed] = useState(false);
  const [genElapsed, setGenElapsed] = useState(0);
  const genTimerRef = useRef(null);
  // Companion presence (breathing/heartbeat) is armed ONLY once she has
  // actually arrived (first voice playback begins) — never at session start.
  const presencePackRef = useRef(null);
  const presenceArmedRef = useRef(false);

  const armPresence = () => {
    if (presenceArmedRef.current || !mixerRef.current || !presencePackRef.current) return;
    presenceArmedRef.current = true;
    try { mixerRef.current.schedulePresenceEvents(presencePackRef.current); } catch (e) { /* ignore */ }
  };

  // Guard: reached without completing the meet flow → send them back gently.
  useEffect(() => {
    if (!characterId || !atmosphere) nav("/meet", { replace: true });
  }, [characterId, atmosphere, nav]);

  // STEP 1 — ambient ASAP + generate the evening in the background
  useEffect(() => {
    if (!atmosphere || startedRef.current) return undefined;
    startedRef.current = true;

    // The world, immediately.
    (async () => {
      try {
        const { data: pack } = await api.get(
          `/atmosphere/${atmosphere?.id || "custom"}/audio_pack`,
          { params: characterId ? { character_id: characterId } : undefined }
        );
        const m = new AmbientMixer();
        mixerRef.current = m;
        await m.start({
          environment: { synth: pack.environment.synth },
          presence: pack.presence || undefined,
        });
        if (ambientAudioRef.current) {
          attachAmbientStreamToAudio(m, ambientAudioRef.current);
        }
        // Iter 69 — silent keep-alive to survive lock-screen suspend.
        try { startKeepAlive(); } catch (e) { /* ignore */ }
        const defaults = pack.environment.default_tags || [];
        if (defaults.length) m.scheduleEvents(defaults);
        // Companion presence (breathing/heartbeat) is deferred until she has
        // ARRIVED — the pack is stashed and armed on first voice playback.
        presencePackRef.current = pack.presence || null;
        if (pack.character_scenario?.length) m.scheduleScenarioEvents(pack.character_scenario);
      } catch (e) {
        console.warn("meet-session: ambient failed to start", e);
      }
    })();

    // The companion's words, generated while she "settles in".
    runGeneration();

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generation — named so the failure state can offer a real "Try again".
  const runGeneration = async () => {
    if (guest.narration) {
      setGenerating(false);
      return;
    }
    setGenFailed(false);
    setGenerating(true);
    try {
      // Cold-start resilience: the trial generation is a single synchronous
      // call — give it a bounded timeout and one retry on transient
      // network/5xx failure so a first-request blip never dead-ends the
      // very first thing a new user tries.
      let data = null;
      let lastErr = null;
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        try {
          ({ data } = await api.post("/guest/generate", {
            user_name: (guest.user_name || "").trim(),
            user_gender: guest.user_gender || null,
            character_id: characterId,
            mode: guest.mode,
            duration_minutes: guest.duration_minutes,
            atmosphere_id: atmosphere?.id,
          }, { timeout: 95000 }));
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          if (status && status < 500 && status !== 408 && status !== 429) throw err;
          console.warn(`meet-session: generate attempt ${attempt}/2 failed`, err?.message);
          if (attempt < 2) await new Promise((r) => setTimeout(r, 2000));
        }
      }
      if (!data) throw lastErr;
      writeGuest({
        narration: data.narration,
        summary: data.summary,
        atmosphere: data.atmosphere,
        relationship_stage: data.relationship_stage,
        session_count: data.session_count,
      });
      setDialogue(data.narration);
    } catch (e) {
      console.warn("meet-session: generate failed", e);
      setGenFailed(true);
    } finally {
      setGenerating(false);
    }
  };

  // Elapsed timer drives the stage copy + progress while generating.
  useEffect(() => {
    if (!generating) {
      if (genTimerRef.current) clearInterval(genTimerRef.current);
      genTimerRef.current = null;
      setGenElapsed(0);
      return undefined;
    }
    const startedAt = Date.now();
    genTimerRef.current = setInterval(
      () => setGenElapsed(Math.round((Date.now() - startedAt) / 1000)),
      1000
    );
    return () => {
      if (genTimerRef.current) clearInterval(genTimerRef.current);
      genTimerRef.current = null;
    };
  }, [generating]);

  // STEP 2 — synthesize the voice once dialogue is ready
  useEffect(() => {
    if (!dialogue) return;
    let cancelled = false;
    (async () => {
      let ok = false;
      for (let attempt = 1; attempt <= 3 && !cancelled; attempt += 1) {
        try {
          const { data } = await api.post("/tts/synthesize", {
            character_id: characterId,
            text: dialogue,
          }, { timeout: 60000 });
          if (!cancelled) setAudioUrl(`${API}${data.url.replace("/api", "")}`);
          ok = true;
          break;
        } catch (e) {
          console.warn(`meet-session: synthesize attempt ${attempt}/3 failed`, e?.message);
          if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 2500));
        }
      }
      // The world continues even if the voice never arrives — but never leave
      // a dead screen: surface the Save card after a short grace period.
      if (!ok && !cancelled) setSavePromptDue(true);
    })();
    return () => { cancelled = true; };
  }, [dialogue, characterId]);

  // STEP 3 — voice fade-in once URL is ready
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return undefined;
    const el = audioRef.current;
    el.volume = 0;
    const p = el.play();
    if (p && p.catch) {
      p.catch(async () => {
        // Autoplay blocked — never ask for a tap. Route the voice through the
        // running WebAudio mixer (already unlocked by the Begin gesture).
        try {
          if (mixerRef.current) {
            await mixerRef.current.playVoiceUrl(audioUrl, { onEnded: () => { setPlaying(false); setSavePromptDue(true); } });
            setPlaying(true);
            setVoiceStarted(true);
            armPresence(); // arrival via the fallback voice path
            return;
          }
        } catch (err) { /* fall through to silent gesture retry */ }
        retryOnNextGesture(() => { if (audioRef.current) audioRef.current.play().catch(() => { /* ignore */ }); });
      });
    }
    // Grace fallback: if playback never manages to begin (hard autoplay
    // block), the Save card still appears after a short while — no dead end.
    const graceTimer = setTimeout(() => setSavePromptDue(true), 15000);
    const startedAt = Date.now();
    const RAMP_MS = 1500;
    if (fadeRef.current) clearInterval(fadeRef.current);
    fadeRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / RAMP_MS);
      if (audioRef.current) audioRef.current.volume = t;
      if (t >= 1) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
      }
    }, 40);
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      clearTimeout(graceTimer);
    };
  }, [audioUrl]);

  // Teardown
  useEffect(() => {
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      if (mixerRef.current) mixerRef.current.stop();
      if (ambientAudioRef.current) {
        try { ambientAudioRef.current.pause(); ambientAudioRef.current.srcObject = null; } catch (e) { /* ignore */ }
      }
      try { stopKeepAlive(); } catch (e) { /* ignore */ }
      clearSessionMetadata();
    };
  }, []);

  // Lock-screen / media-session metadata for the guest's first meeting.
  const [characterImage, setCharacterImage] = useState(null);
  useEffect(() => {
    api.get("/catalog/characters")
      .then((r) => setCharacterImage(r.data.find((x) => x.id === characterId)?.image || null))
      .catch(() => {});
  }, [characterId]);
  useEffect(() => {
    if (!atmosphere) return;
    const characterName = characterId === "seriny" ? "Serine" : "Elizabeth";
    setSessionMetadata({
      characterName,
      atmosphereName: atmosphere?.name,
      kind: "evening",
      artworkUrl: characterImage || undefined,
    });
    bindMediaSessionControls({
      onPlay: () => { if (audioRef.current) audioRef.current.play().catch(() => { /* ignore */ }); },
      onPause: () => { if (audioRef.current) audioRef.current.pause(); },
    });
  }, [atmosphere, characterId, characterImage]);

  // Pause/resume couples both layers
  const togglePlay = async () => {
    // ONE canonical control path: always drive BOTH the voice element and the
    // mixer, and always settle the UI state (the old early-return branches
    // could desync `playing` when play() was rejected on device).
    const next = !playing;
    try {
      if (next) {
        if (mixerRef.current) await mixerRef.current.resume();
        if (audioRef.current && audioUrl && !audioRef.current.ended) {
          try { await audioRef.current.play(); } catch (e) { /* mixer resumed regardless */ }
        }
      } else {
        if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* ignore */ } }
        if (mixerRef.current) await mixerRef.current.pause();
      }
    } finally {
      setPlaying(next);
    }
  };

  const onAudioPlay = () => {
    setPlaying(true);
    setVoiceStarted(true);
    if (mixerRef.current) { mixerRef.current.resume(); mixerRef.current.setDialogueActive(true); }
    armPresence(); // she has arrived — presence (breathing) may begin, ducked under her voice
  };
  const onAudioPause = () => { setPlaying(false); if (mixerRef.current) mixerRef.current.pause(); };
  const onAudioEnded = () => {
    setPlaying(false);
    if (mixerRef.current) mixerRef.current.setDialogueActive(false);
    setSavePromptDue(true); // trial reached its natural completion → CTA immediately
  };

  const leave = async () => {
    if (mixerRef.current) await mixerRef.current.stop();
    nav("/meet");
  };

  if (!atmosphere) return null;

  const characterName = characterId === "seriny" ? "Serine" : "Elizabeth";

  return (
    <div className="min-h-screen relative text-violet-50" data-testid="meet-session-screen">
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-8 pt-safe-top pb-44 min-h-screen flex flex-col">
        <button
          type="button"
          onClick={leave}
          data-testid="meet-session-back-button"
          className="inline-flex items-center gap-2 text-sm text-violet-300/60 hover:text-violet-50 mb-12 transition-soft self-start"
        >
          <ArrowLeft className="w-4 h-4" /> Quiet
        </button>

        <div className="mb-10 fade-rise flex-1 flex flex-col justify-center text-center">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-violet-300/80 mb-4 justify-center">
            <Moon className="w-3 h-3" /> First meeting · {MODE_LABEL[guest.mode] || "Sleep"}
          </div>
          {genFailed ? (
            <>
              <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-3" data-testid="meet-session-failed">
                The fire wouldn't quite catch.
              </h1>
              <p className="text-violet-200/75 text-sm mt-2 max-w-md mx-auto leading-relaxed">
                Something interrupted her on the way to you. It happens — try once more.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <button
                  type="button"
                  onClick={runGeneration}
                  data-testid="meet-session-retry"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft glow-violet"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={() => nav("/meet")}
                  data-testid="meet-session-change-scene"
                  className="inline-flex items-center justify-center rounded-full px-7 py-3 border border-violet-300/30 text-violet-200 hover:border-violet-300/60 hover:text-violet-50 transition-soft"
                >
                  Choose a different scene
                </button>
              </div>
            </>
          ) : generating ? (
            <>
              {characterImage && (
                <img
                  src={characterImage}
                  alt=""
                  aria-hidden="true"
                  data-testid="meet-session-generating-portrait"
                  className="w-24 h-24 rounded-full object-cover mx-auto mb-8 border border-violet-300/25"
                  style={{ animation: "breathe 4.8s ease-in-out infinite" }}
                />
              )}
              <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-3" data-testid="meet-session-lighting">
                Lighting the fire<span className="italic text-violet-200/80 breathe-dots">…</span>
              </h1>
              <p className="text-violet-200/75 text-sm mt-1" data-testid="meet-session-stage">
                {stageFor(genElapsed)}…
              </p>
              <div
                className="w-full max-w-xs mx-auto mt-6 h-1 rounded-full bg-violet-300/15 overflow-hidden"
                data-testid="meet-session-progress"
                role="progressbar"
              >
                <div
                  className="h-full bg-violet-300/70 rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${Math.min(92, Math.round((1 - Math.exp(-genElapsed / 35)) * 100))}%` }}
                />
              </div>
            </>
          ) : (
            <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-3" data-testid="meet-session-location">
              You're with her <span className="italic text-violet-200/80">at the {atmosphere?.name?.toLowerCase().replace(/^the\s+/, "")}</span>.
            </h1>
          )}
          {!genFailed && (
            <p className="text-violet-300/70 text-sm flex items-center gap-2 mt-3 justify-center">
              <Sparkles className="w-3 h-3 text-violet-300/70" />
              {generating
                ? "The world is waking around you"
                : !voiceStarted && !savePromptDue
                  ? "Warming her voice — she'll speak in a moment"
                  : "Trial session — not saved yet"}
            </p>
          )}
        </div>

        {/* Save prompt — appears once she has actually spoken (grace fallback
            covers blocked/failed audio so the screen never dead-ends). */}
        {!generating && (voiceStarted || savePromptDue) && (
          <div className="glass-strong rounded-2xl p-7 fade-rise" data-testid="meet-save-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-violet-300/90 mb-4">
              <BookmarkPlus className="w-3 h-3" /> Keep this thread
            </div>
            <h2 className="font-serif-display text-2xl md:text-3xl mb-3">Save your story.</h2>
            <p className="text-violet-200/80 mb-6 leading-relaxed max-w-xl">
              Create an account so {characterName} can remember you and continue your story tomorrow.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/save"
                data-testid="meet-session-save-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft glow-violet"
              >
                Save and continue
              </Link>
              <button
                type="button" onClick={() => nav("/meet")}
                data-testid="meet-session-restart"
                className="inline-flex items-center justify-center rounded-full px-7 py-3 border border-violet-300/30 text-violet-200 hover:border-violet-300/60 hover:text-violet-50 transition-soft"
              >
                Try a different scene
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Real audio element — auto-plays with fade-in */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          autoPlay
          playsInline
          onPlay={onAudioPlay}
          onPause={onAudioPause}
          onEnded={onAudioEnded}
          data-testid="meet-session-audio"
        />
      )}
      {/* Hidden ambient mirror — lock-screen-resilient */}
      <audio
        ref={ambientAudioRef}
        playsInline
        autoPlay
        style={{ display: "block", position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }}
        data-testid="meet-ambient-audio"
      />

      {/* Floating pause/resume — single control */}
      <div
        className="fixed bottom-0 left-0 right-0 z-20 px-6 pointer-events-none"
        style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="max-w-md mx-auto flex justify-center pointer-events-auto">
          <button
            type="button"
            onClick={togglePlay}
            data-testid="meet-audio-play-button"
            aria-label={playing ? "Pause" : "Resume"}
            className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-soft ${playing ? "bg-violet-300/85 text-indigo-950 hover:bg-violet-200" : "bg-violet-200 text-indigo-950 hover:bg-violet-100"} glow-violet`}
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
